import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { applyUpdate } from '../serviceWorkerRegistration';
import { Workbox } from 'workbox-window';

interface VersionInfo {
  version: string;
  buildTime: string;
  buildId: string;
}

const UpdateNotification: React.FC = () => {
  // State to track whether an update is available
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [serviceWorker, setServiceWorker] = useState<Workbox | null>(null);
  const [, setVersionInfo] = useState<VersionInfo | null>(null);

  // Check for version updates periodically
  useEffect(() => {
    let cachedBuildId: string | null = null;
    
    const checkForUpdates = async () => {
      try {
        // Add cache-busting query parameter to prevent caching
        const response = await fetch(`/version.json?cacheBust=${Date.now()}`);
        if (response.ok) {
          const data: VersionInfo = await response.json();
          setVersionInfo(data);

          // If we have a cached buildId and it's different, we have an update
          if (cachedBuildId && cachedBuildId !== data.buildId) {
            console.log('New version detected:', data.buildId);
            setShowNotification(true);
          }
          
          // Store the buildId for future comparisons
          cachedBuildId = data.buildId;
          
          // Store in localStorage for offline comparison
          localStorage.setItem('appBuildId', data.buildId);
          localStorage.setItem('appVersion', data.version);
        }
      } catch (error) {
        console.error('Failed to check for updates:', error);
      }
    };

    // Check immediately on mount
    checkForUpdates();
    
    // Set up periodic checks (every 30 minutes)
    const intervalId = setInterval(checkForUpdates, 30 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    // Listen for update available events from service worker
    const handleUpdateAvailable = (event: CustomEvent) => {
      console.log('Update available event received');
      setShowNotification(true);
      setServiceWorker(event.detail.serviceWorker);
    };

    // Add event listener for update notifications
    window.addEventListener('updateAvailable', handleUpdateAvailable as EventListener);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('updateAvailable', handleUpdateAvailable as EventListener);
    };
  }, []);

  // Handle update button click
  const handleUpdate = () => {
    if (serviceWorker) {
      applyUpdate(serviceWorker);
    } else {
      // If no service worker is available, just reload the page
      window.location.reload();
    }
  };

  // If no notification to show, don't render anything
  if (!showNotification) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed top-0 left-0 right-0 z-50 p-4 bg-blue-600 text-white shadow-lg flex flex-col items-center justify-center"
    >
      <h3 className="text-lg font-semibold mb-2">Update Available</h3>
      <p className="text-sm mb-4">A new version of the app is available. Please update now.</p>
      <button
        onClick={handleUpdate}
        className="px-4 py-2 bg-white text-blue-600 rounded font-semibold hover:bg-blue-100 transition-colors"
      >
        Update Now
      </button>
    </motion.div>
  );
};

export default UpdateNotification; 